/**
 * Statistical classification methods for choropleth/graduated color scales.
 *
 * Implements common GIS classification algorithms:
 * - Natural Breaks (Jenks) — minimizes within-class variance
 * - Quantiles — equal count per class
 * - Equal Interval — even spacing across the data range
 */

// --- Types ---

export type ClassificationMethod =
  | 'fixed'
  | 'natural_breaks'
  | 'quantiles'
  | 'equal_interval';

export const CLASSIFICATION_METHODS: {
  value: ClassificationMethod;
  label: string;
}[] = [
  { value: 'fixed', label: 'Fixed' },
  { value: 'natural_breaks', label: 'Natural Breaks' },
  { value: 'quantiles', label: 'Quantiles' },
  { value: 'equal_interval', label: 'Equal Interval' },
];

export interface ClassBreak {
  /** Lower bound (inclusive) */
  min: number;
  /** Upper bound (exclusive, except for last class) */
  max: number;
}

// --- Algorithms ---

/**
 * Jenks Natural Breaks optimization.
 * Finds class boundaries that minimize within-class variance.
 *
 * @param values - Sorted array of numeric values
 * @param numClasses - Number of classes to produce
 * @returns Array of break points (numClasses - 1 values)
 */
export function jenksBreaks(values: number[], numClasses: number): number[] {
  const n = values.length;
  if (n === 0) return [];
  if (numClasses >= n) return values.slice(0, n - 1);

  const sorted = [...values].sort((a, b) => a - b);

  // Initialize matrices for the dynamic programming
  const lowerClassLimits: number[][] = Array.from({ length: n + 1 }, () =>
    Array(numClasses + 1).fill(0),
  );
  const varianceCombinations: number[][] = Array.from({ length: n + 1 }, () =>
    Array(numClasses + 1).fill(Infinity),
  );

  for (let i = 1; i <= numClasses; i++) {
    lowerClassLimits[1][i] = 1;
    varianceCombinations[1][i] = 0;
  }

  for (let l = 2; l <= n; l++) {
    let sum = 0;
    let sumSquares = 0;

    for (let m = 1; m <= l; m++) {
      const lowerClassLimit = l - m + 1;
      const val = sorted[lowerClassLimit - 1];
      sum += val;
      sumSquares += val * val;

      const variance = sumSquares - (sum * sum) / m;
      const id = lowerClassLimit - 1;

      if (id !== 0) {
        for (let j = 2; j <= numClasses; j++) {
          const candidate = variance + varianceCombinations[id][j - 1];
          if (candidate < varianceCombinations[l][j]) {
            lowerClassLimits[l][j] = lowerClassLimit;
            varianceCombinations[l][j] = candidate;
          }
        }
      }
    }

    lowerClassLimits[l][1] = 1;
    varianceCombinations[l][1] = sumSquares - (sum * sum) / l;
  }

  // Read back the breaks
  const breaks: number[] = Array(numClasses - 1).fill(0);
  let k = n;
  for (let j = numClasses; j >= 2; j--) {
    const id = lowerClassLimits[k][j] - 1;
    breaks[j - 2] = sorted[id];
    k = lowerClassLimits[k][j] - 1;
  }

  return breaks;
}

/**
 * Quantile breaks — each class has approximately the same number of values.
 *
 * @param values - Array of numeric values
 * @param numClasses - Number of classes to produce
 * @returns Array of break points (numClasses - 1 values)
 */
export function quantileBreaks(values: number[], numClasses: number): number[] {
  if (values.length === 0) return [];
  const sorted = [...values].sort((a, b) => a - b);
  const breaks: number[] = [];

  for (let i = 1; i < numClasses; i++) {
    const idx = Math.floor((i / numClasses) * sorted.length);
    const breakVal = sorted[Math.min(idx, sorted.length - 1)];
    // Avoid duplicate breaks
    if (breaks.length === 0 || breakVal > breaks[breaks.length - 1]) {
      breaks.push(breakVal);
    }
  }

  return breaks;
}

/**
 * Equal Interval breaks — evenly spaced between min and max.
 *
 * @param values - Array of numeric values
 * @param numClasses - Number of classes to produce
 * @returns Array of break points (numClasses - 1 values)
 */
export function equalIntervalBreaks(
  values: number[],
  numClasses: number,
): number[] {
  if (values.length === 0) return [];
  const min = Math.min(...values);
  const max = Math.max(...values);
  if (min === max) return [];

  const interval = (max - min) / numClasses;
  const breaks: number[] = [];

  for (let i = 1; i < numClasses; i++) {
    breaks.push(Math.round((min + interval * i) * 10) / 10);
  }

  return breaks;
}

/**
 * Compute class breaks using the specified method.
 *
 * @param values - Array of numeric values (will be filtered for NaN/Infinity)
 * @param method - Classification method to use
 * @param numClasses - Number of classes (default: 6)
 * @returns Array of break points (numClasses - 1 values)
 */
export function computeBreaks(
  values: number[],
  method: ClassificationMethod,
  numClasses = 6,
): number[] {
  const clean = values.filter((v) => Number.isFinite(v));
  if (clean.length < 2) return [];

  switch (method) {
    case 'natural_breaks':
      return jenksBreaks(clean, numClasses);
    case 'quantiles':
      return quantileBreaks(clean, numClasses);
    case 'equal_interval':
      return equalIntervalBreaks(clean, numClasses);
    case 'fixed':
    default:
      return [];
  }
}
